/* cache-bust 20260807 */
/* cache-bust-20260807
 * sr-model.js v1.3 — 数据层（Model）
 * 最后修改: 2026-08-03 v1.3
 * 
 * v1.3 新增：
 *   - avatar_url 字段加入 _data 和 PERSIST_FIELDS
 *   - addressAdd(addr) — 新增地址到 KV（POST /api/address）
 *   - addressUpdate(idx, addr) — 更新地址（PUT /api/address）
 *   - addressDelete(idx) — 删除地址（DELETE /api/address）
 *   - addressSetDefault(idx) — 设为默认地址
 *   - orderNoteGet(orderId) — 获取订单备注（localStorage 缓存 + order 对象）
 *   - orderNoteSave(orderId, note) — 保存订单备注到 localStorage
 * 
 * 职责：三层存储管理 + KV 交互 + 状态监控（纯读写，不含业务判断）
 *   索引层：Cookie（sr_member + sr_prefs，跨会话认人凭证）
 *   数据层：sessionStorage（sr_data，本次到店完整快照，跨页面）
 *   沙盒层：localStorage（sr_cart_sandbox，购物车抽屉内缓冲，不跨页面）
 * 
 * 原则：
 *   - KV 只在到店首尾各碰一次（init 读一次，persist 写一次）
 *   - sessionStorage 是本次到店的唯一数据源
 *   - localStorage 不出购物车抽屉（order_note 例外，需跨会话保留）
 *   - 整个会话只有一个"当前身份"，不产生冗余 MID
 *   - Cookie 只在 persist()/persistNow() 时写入，set() 不碰 Cookie
 * 
 * 对外 API（供 Controller 调用）：
 *   SRModel.init()               → 到店初始化
 *   SRModel.get()                → 读完整数据（副本）
 *   SRModel.set(data)            → 写 sessionStorage（增量合并）
 *   SRModel.persist()            → 写 KV + 同步 Cookie（防抖）
 *   SRModel.persistNow()         → 立即写 KV
 *   SRModel.reset()              → 清空所有层
 *   SRModel.cookie               → Cookie 读写
 *   SRModel.session              → sessionStorage 读写
 *   SRModel.local                → localStorage 读写
 *   SRModel.watch(cb)            → 监听数据变化
 *   SRModel.unwatch(id)          → 取消监听
 *   SRModel.addressAdd(addr)     → 新增地址（调 Worker API）
 *   SRModel.addressUpdate(idx,a) → 更新地址
 *   SRModel.addressDelete(idx)   → 删除地址
 *   SRModel.addressSetDefault(i) → 设默认地址
 *   SRModel.orderNoteGet(oid)    → 取订单备注
 *   SRModel.orderNoteSave(oid,n) → 存订单备注
 * 
 * 依赖：无。纯数据层。
 */

(function() {
  'use strict';

  // ============================================================
  // 常量
  // ============================================================

  var COOKIE_MEMBER = 'sr_member';
  var COOKIE_PREFS  = 'sr_prefs';
  var SESSION_KEY   = 'sr_data';
  var SESSION_ACTIVE_KEY = 'sr_session_active';
  var LOCAL_KEY     = 'sr_cart_sandbox';
  var NOTE_PREFIX   = 'sr_order_note:';
  var WORKER_URL    = 'https://workerapi.sandalwoodritual.com';
  var COOKIE_MAX_AGE = 31536000; // 1 年（秒）

  // 需要持久化到 KV 的字段（address/orders 是独立 KV key，不在此列）
  var PERSIST_FIELDS = [
    'mid', 'email', 'first_name', 'last_name', 'phone', 'joined', 'verified', 'avatar_url'
  ];

  // ============================================================
  // 内部状态
  // ============================================================

  var _data = {
    mid: '',
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    joined: '',
    verified: false,
    avatar_url: '',
    addresses: [],
    default_address_idx: -1,
    orders: []
  };

  var _watchers = [];
  var _watcherId = 0;
  var _persistTimer = null;
  var _initialized = false;

  // ============================================================
  // Cookie（索引层）
  // ============================================================

  function cookie_read() {
    try {
      var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_PREFS + '=([^;]*)'));
      if (m) {
        var prefs = JSON.parse(decodeURIComponent(m[1]));
        return (prefs && prefs.mid) ? prefs.mid : '';
      }
    } catch (e) {}
    return '';
  }

  function cookie_write(arg) {
    try {
      var hostname = location.hostname;
      var domain = hostname.indexOf('www.') === 0 ? hostname.substring(4) : hostname;
      if (domain.indexOf('.') !== 0) domain = '.' + domain;

      if (arg && typeof arg === 'object' && arg.mid) {
        document.cookie = COOKIE_MEMBER + '=true;path=/;domain=' + domain + ';max-age=' + COOKIE_MAX_AGE + ';SameSite=Lax';
        var prefs = JSON.stringify({ mid: arg.mid, email: arg.email || '' });
        document.cookie = COOKIE_PREFS + '=' + encodeURIComponent(prefs) +
          ';path=/;domain=' + domain + ';max-age=' + COOKIE_MAX_AGE + ';SameSite=Lax';
      } else {
        document.cookie = COOKIE_MEMBER + '=;path=/;domain=' + domain + ';max-age=0';
        document.cookie = COOKIE_PREFS + '=;path=/;domain=' + domain + ';max-age=0';
      }
    } catch (e) {
      console.error('[SRModel] cookie_write error:', e);
    }
  }

  function session_active_clear() {
    try { sessionStorage.removeItem(SESSION_ACTIVE_KEY); } catch (e) {}
  }

  // ============================================================
  // sessionStorage（数据层）
  // ============================================================

  function session_read() {
    try {
      var raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return null;
  }

  function session_write(data) {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('[SRModel] session_write error:', e);
    }
  }

  function session_clear() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) {}
  }

  // ============================================================
  // localStorage（沙盒层 — 购物车抽屉专用 + 订单备注）
  // ============================================================

  function local_read() {
    try {
      var raw = localStorage.getItem(LOCAL_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {}
    return null;
  }

  function local_write(data) {
    try {
      localStorage.setItem(LOCAL_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('[SRModel] local_write error:', e);
    }
  }

  function local_clear() {
    try { localStorage.removeItem(LOCAL_KEY); } catch (e) {}
  }

  // ============================================================
  // 通用 XHR 工具
  // ============================================================

  function xhr_request(method, url, body) {
    return new Promise(function(resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(method, url);
      if (body && typeof body === 'object' && !(body instanceof FormData)) {
        xhr.setRequestHeader('Content-Type', 'application/json');
      }
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (e) { resolve(xhr.responseText); }
        } else {
          try {
            var err = JSON.parse(xhr.responseText);
            reject(new Error(err.error || 'Request failed: ' + xhr.status));
          } catch (e2) {
            reject(new Error('Request failed: ' + xhr.status));
          }
        }
      };
      xhr.onerror = function() { reject(new Error('Network error')); };
      var payload = null;
      if (body) {
        if (typeof body === 'object' && !(body instanceof FormData)) {
          payload = JSON.stringify(body);
        } else {
          payload = body;
        }
      }
      xhr.send(payload);
    });
  }

  // ============================================================
  // KV 交互（到店首尾各碰一次）
  // ============================================================

  function kv_fetch(mid) {
    return new Promise(function(resolve) {
      var xhr = new XMLHttpRequest();
      xhr.open('GET', WORKER_URL + '/api/member?mid=' + encodeURIComponent(mid));
      xhr.onload = function() {
        if (xhr.status === 200) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (e) { resolve(null); }
        } else {
          resolve(null);
        }
      };
      xhr.onerror = function() { resolve(null); };
      xhr.send();
    });
  }

  function kv_sync() {
    return new Promise(function(resolve, reject) {
      var payload = {};
      for (var i = 0; i < PERSIST_FIELDS.length; i++) {
        var k = PERSIST_FIELDS[i];
        if (_data[k] !== undefined) payload[k] = _data[k];
      }

      var xhr = new XMLHttpRequest();
      xhr.open('POST', WORKER_URL + '/api/member/sync');
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.onload = function() {
        if (xhr.status === 200) {
          try { resolve(JSON.parse(xhr.responseText)); }
          catch (e) { reject(e); }
        } else {
          reject(new Error('KV sync failed: ' + xhr.status));
        }
      };
      xhr.onerror = function() { reject(new Error('KV sync network error')); };
      xhr.send(JSON.stringify(payload));
    });
  }

  // ============================================================
  // 地址簿 KV 交互（独立 KV key: addr:{mid}）
  // ============================================================

  /**
   * 从 Worker 拉取地址簿并更新 _data
   * Worker 返回格式: { mid, addresses: [], default_address_id: null }
   * Model 内部存储: addresses[] + default_address_idx (number)
   */
  function kv_fetchAddresses(mid) {
    return xhr_request('GET', WORKER_URL + '/api/address?mid=' + encodeURIComponent(mid)).then(function(resp) {
      var addresses = (resp && resp.addresses) ? resp.addresses : [];
      var defaultId = (resp && resp.default_address_id) ? resp.default_address_id : null;
      var defaultIdx = -1;
      if (defaultId) {
        for (var i = 0; i < addresses.length; i++) {
          if (addresses[i].id === defaultId) { defaultIdx = i; break; }
        }
      }
      _data.addresses = addresses;
      _data.default_address_idx = defaultIdx;
      session_write(_data);
      notifyWatchers('addresses');
      return { addresses: addresses.slice(), default_address_idx: defaultIdx };
    });
  }

  // ============================================================
  // 初始化流程（到店）
  // ============================================================

  function init() {
    if (_initialized) {
      return Promise.resolve(getCopy());
    }

    // 【核心修正】先清空 sessionStorage，确保本次会话从白板开始
    // 防止上次会话的脏数据影响本次会话的身份判断
    session_clear();
    local_clear();

    var mid = cookie_read();

    // 有 Cookie（钥匙）→ 直接查 KV 验证身份，不信任 sessionStorage
    if (mid) {
      return kv_fetch(mid).then(function(member) {
        if (member && member.mid) {
          // KV 有数据 → 填充 sessionStorage，成为 Insider
          importData(member);
          console.log('[SRModel] init: Cookie valid, KV hit → Insider', mid);
        } else {
          // KV 无数据（如邮箱退信已被删）→ Cookie 作废，SessionStorage 保持白板
          _data = getEmptyData();
          session_write(_data);
          cookie_write('');  // 作废 Cookie
          console.log('[SRModel] init: Cookie invalid (KV miss), cookie cleared → Explorer');
        }
        _initialized = true;
        return getCopy();
      });
    }

    // 无 Cookie → 新访客或 Cookie 已过期，SessionStorage 白板，身份为 Explorer
    _data = getEmptyData();
    session_write(_data);
    _initialized = true;
    console.log('[SRModel] init: no cookie → new visitor → Explorer');
    return Promise.resolve(getCopy());
  }

  // ============================================================
  // 内部工具
  // ============================================================

  function getEmptyData() {
    return {
      mid: '', email: '', first_name: '', last_name: '', phone: '',
      joined: '', verified: false, avatar_url: '',
      addresses: [], default_address_idx: -1, orders: []
    };
  }

  function importData(member) {
    _data.mid        = member.mid || '';
    _data.email      = member.email || '';
    _data.first_name = member.first_name || '';
    _data.last_name  = member.last_name || '';
    _data.phone      = member.phone || '';
    _data.joined     = member.joined || '';
    _data.verified   = member.verified || false;
    _data.avatar_url = member.avatar_url || member.avatar || '';
    _data.addresses  = member.addresses || [];
    _data.default_address_idx = member.default_address_idx !== undefined ? member.default_address_idx : -1;
    _data.orders     = member.orders || [];
    session_write(_data);
  }

  function getCopy() {
    var copy = {};
    for (var k in _data) {
      if (_data.hasOwnProperty(k)) {
        copy[k] = Array.isArray(_data[k]) ? _data[k].slice() : _data[k];
      }
    }
    return copy;
  }

  function notifyWatchers(changeType) {
    var snapshot = getCopy();
    for (var i = 0; i < _watchers.length; i++) {
      try { _watchers[i].fn(snapshot, changeType); }
      catch (e) { console.error('[SRModel] watcher error:', e); }
    }
  }

  // ============================================================
  // 地址簿 CRUD（调 Worker API + 更新 _data）
  // ============================================================

  /**
   * 新增地址
   * @param {Object} addr — { label, address1, address2, city, state, zip, country, phone, set_default }
   * @returns {Promise<Object>} 新地址对象
   */
  function addressAdd(addr) {
    var mid = _data.mid;
    if (!mid) return Promise.reject(new Error('No mid'));
    var payload = Object.assign({ mid: mid }, addr);
    return xhr_request('POST', WORKER_URL + '/api/address', payload).then(function(resp) {
      // 拉取最新地址簿
      return kv_fetchAddresses(mid).then(function() {
        return resp;
      });
    });
  }

  /**
   * 更新地址
   * @param {number} idx — 地址在 _data.addresses 中的索引
   * @param {Object} addr — 要更新的字段
   * @returns {Promise<Object>} 更新后的地址
   */
  function addressUpdate(idx, addr) {
    var mid = _data.mid;
    if (!mid) return Promise.reject(new Error('No mid'));
    if (idx < 0 || idx >= _data.addresses.length) return Promise.reject(new Error('Invalid address index'));
    var addrId = _data.addresses[idx].id;
    if (!addrId) return Promise.reject(new Error('Address has no id'));
    var payload = Object.assign({ mid: mid, address_id: addrId }, addr);
    return xhr_request('PUT', WORKER_URL + '/api/address', payload).then(function(resp) {
      return kv_fetchAddresses(mid).then(function() {
        return resp;
      });
    });
  }

  /**
   * 删除地址
   * @param {number} idx — 地址索引
   * @returns {Promise<Object>} { success, remaining }
   */
  function addressDelete(idx) {
    var mid = _data.mid;
    if (!mid) return Promise.reject(new Error('No mid'));
    if (idx < 0 || idx >= _data.addresses.length) return Promise.reject(new Error('Invalid address index'));
    var addrId = _data.addresses[idx].id;
    if (!addrId) return Promise.reject(new Error('Address has no id'));
    return xhr_request('DELETE', WORKER_URL + '/api/address', { mid: mid, address_id: addrId }).then(function(resp) {
      return kv_fetchAddresses(mid).then(function() {
        return resp;
      });
    });
  }

  /**
   * 设为默认地址
   * @param {number} idx — 地址索引
   * @returns {Promise<Object>} 更新结果
   */
  function addressSetDefault(idx) {
    var mid = _data.mid;
    if (!mid) return Promise.reject(new Error('No mid'));
    if (idx < 0 || idx >= _data.addresses.length) return Promise.reject(new Error('Invalid address index'));
    var addrId = _data.addresses[idx].id;
    if (!addrId) return Promise.reject(new Error('Address has no id'));
    return xhr_request('PUT', WORKER_URL + '/api/address', {
      mid: mid, address_id: addrId, set_default: true
    }).then(function(resp) {
      return kv_fetchAddresses(mid).then(function() {
        return resp;
      });
    });
  }

  // ============================================================
  // 订单备注（localStorage 持久化，跨会话保留）
  // ============================================================

  /**
   * 获取订单备注
   * 先查 localStorage（用户编辑过的），再查 order 对象的 note 字段
   * @param {string} orderId
   * @returns {string} 备注文本
   */
  function orderNoteGet(orderId) {
    if (!orderId) return '';
    try {
      var note = localStorage.getItem(NOTE_PREFIX + orderId);
      if (note !== null) return note;
    } catch (e) {}
    // 查找 order 对象中的 note 字段
    for (var i = 0; i < _data.orders.length; i++) {
      if (_data.orders[i].order_number === orderId || _data.orders[i].order_id === orderId) {
        return _data.orders[i].note || '';
      }
    }
    return '';
  }

  /**
   * 保存订单备注到 localStorage
   * @param {string} orderId
   * @param {string} note
   */
  function orderNoteSave(orderId, note) {
    if (!orderId) return;
    try {
      if (note && note.trim()) {
        localStorage.setItem(NOTE_PREFIX + orderId, note.trim());
      } else {
        localStorage.removeItem(NOTE_PREFIX + orderId);
      }
    } catch (e) {
      console.error('[SRModel] orderNoteSave error:', e);
    }
  }

  // ============================================================
  // 公共 API
  // ============================================================

  window.SRModel = {

    // ── 生命周期 ──

    init: init,

    reset: function() {
      _data = getEmptyData();
      session_clear();
      session_active_clear();
      local_clear();
      cookie_write('');
      _initialized = false;
      notifyWatchers('reset');
      console.log('[SRModel] reset');
    },

    // ── 数据读写 ──

    get: function() {
      return getCopy();
    },

    set: function(data) {
      if (!data || typeof data !== 'object') return;

      var changed = false;
      for (var key in data) {
        if (data.hasOwnProperty(key) && key in _data) {
          if (_data[key] !== data[key]) {
            _data[key] = data[key];
            changed = true;
          }
        }
      }

      if (!changed) return;

      session_write(_data);
      notifyWatchers('set');
      console.log('[SRModel] set:', Object.keys(data).join(', '));
    },

    persist: function() {
      if (_persistTimer) clearTimeout(_persistTimer);
      _persistTimer = setTimeout(function() {
        kv_sync().then(function(resp) {
          if (resp && resp.mid && resp.mid !== _data.mid) {
            _data.mid = resp.mid;
            session_write(_data);
            notifyWatchers('mid-update');
          }
          cookie_write({ mid: _data.mid, email: _data.email });
          console.log('[SRModel] persist: KV written + Cookie synced');
        }).catch(function(err) {
          console.error('[SRModel] persist failed:', err);
        });
      }, 500);
    },

    persistNow: function() {
      if (_persistTimer) clearTimeout(_persistTimer);
      return kv_sync().then(function(resp) {
        if (resp && resp.mid && resp.mid !== _data.mid) {
          _data.mid = resp.mid;
          session_write(_data);
          notifyWatchers('mid-update');
        }
        cookie_write({ mid: _data.mid, email: _data.email });
        console.log('[SRModel] persistNow: KV written + Cookie synced');
        return resp;
      });
    },

    // ── 购物车沙盒（localStorage 生命周期管理） ──
    // cartOpen: 快照 sessionStorage → localStorage
    // cartSave: 修改 localStorage 中的数据
    // cartClose: 读 localStorage → 清 localStorage → 返回数据（C 层合并回 sessionStorage）

    cartOpen: function() {
      local_write(Object.assign({}, _data, { timestamp: Date.now() }));
      saveAddressesToStorage(_data);
    },

    cartClose: function() {
      var sandbox = local_read();
      local_clear();
      window._srCartSnapshot = sandbox;  // 缓存供 getSnapshot 用
      return sandbox;
    },

    cartSave: function(patch) {
      var sandbox = local_read() || {};
      for (var k in patch) { sandbox[k] = patch[k]; }
      sandbox.timestamp = Date.now();
      local_write(sandbox);
    },

    cartSnapshot: function() {
      return window._srCartSnapshot || local_read();
    },

    // ── 出口写盘：beforeunload 专用 ──

    persistOnExit: function() {
      if (!_data.mid || _data.mid === 'visitor') return;
      // cookie 同步（同步操作，浏览器保证完成）
      cookie_write({ mid: _data.mid, email: _data.email || '' });
      // KV 写盘（sendBeacon，best-effort）
      try {
        var payload = {};
        for (var i = 0; i < PERSIST_FIELDS.length; i++) {
          var k = PERSIST_FIELDS[i];
          if (_data[k] !== undefined) payload[k] = _data[k];
        }
        payload._ts = Date.now();
        navigator.sendBeacon && navigator.sendBeacon(WORKER_URL + '/api/member/sync', JSON.stringify(payload));
      } catch(e) { /* best-effort */ }
      console.log('[SRModel] persistOnExit: cookie + KV beacon sent');
    },

    // ── 监控 ──

    watch: function(callback) {
      var id = ++_watcherId;
      _watchers.push({ id: id, fn: callback });
      return id;
    },

    unwatch: function(id) {
      _watchers = _watchers.filter(function(w) { return w.id !== id; });
    },

    // ── v1.3: 地址簿 CRUD ──

    addressAdd: addressAdd,
    addressUpdate: addressUpdate,
    addressDelete: addressDelete,
    addressSetDefault: addressSetDefault,

    // ── v1.3: 订单备注 ──

    orderNoteGet: orderNoteGet,
    orderNoteSave: orderNoteSave
  };

  // ═══════════════════════════════════════════
  // M 层指令注册（通过 SRCommand.onM）
  // 在 SRCommand 加载后自动注册
  // ═══════════════════════════════════════════

  function registerCommands() {
    if (typeof SRCommand === 'undefined') {
      console.warn('[SRModel] SRCommand not loaded yet, retrying...');
      setTimeout(registerCommands, 100);
      return;
    }

    var CMD = SRCommand.CMD;
    var dispatch = SRCommand;

    // ── 邮箱注册/登录 → 写 sessionStorage ──
    dispatch.onM(CMD.EMAIL_REGISTER, function(p) {
      SRModel.set(p);
      console.log('[M] email:register → sessionStorage updated');
    });

    dispatch.onM(CMD.EMAIL_LOGIN, function(p) {
      SRModel.set(p);
      console.log('[M] email:login → sessionStorage updated');
    });

    // ── OTP 验证通过 → 写 sessionStorage ──
    dispatch.onM(CMD.OTP_VERIFY_OK, function(p) {
      SRModel.set(p);
      console.log('[M] otp:ok → sessionStorage updated');
    });

    // ── 购物车沙盒（通过 Model 公开方法） ──
    dispatch.onM(CMD.CART_OPEN, function() {
      SRModel.cartOpen();
    });

    dispatch.onM(CMD.CART_SAVE, function(p) {
      SRModel.cartSave(p);
    });

    dispatch.onM(CMD.CART_CLOSE, function() {
      return SRModel.cartClose();
    });

    dispatch.onM(CMD.CART_DISCARD, function() {
      localStorage.removeItem('sr_cart_sandbox');
    });

    // ── 个人信息 ──
    dispatch.onM(CMD.PROFILE_UPDATE, function(p) {
      SRModel.set(p);
    });

    dispatch.onM(CMD.AVATAR_SET, function(p) {
      SRModel.set({ avatar_url: p.url });
    });

    dispatch.onM(CMD.AVATAR_UPLOAD_OK, function(p) {
      SRModel.set({ avatar_url: p.avatar_url });
    });

    dispatch.onM(CMD.ADDRESS_ADD, function(p) {
      var cur = SRModel.get();
      var addrs = cur.addresses || [];
      addrs.push(p.address);
      SRModel.set({ addresses: addrs });
    });

    dispatch.onM(CMD.ADDRESS_UPDATE, function(p) {
      var cur = SRModel.get();
      if (cur.addresses && cur.addresses[p.index]) {
        cur.addresses[p.index] = p.address;
        SRModel.set({ addresses: cur.addresses });
      }
    });

    dispatch.onM(CMD.ADDRESS_DELETE, function(p) {
      var cur = SRModel.get();
      var addrs = cur.addresses || [];
      addrs.splice(p.index, 1);
      SRModel.set({ addresses: addrs });
    });

    dispatch.onM(CMD.ADDRESS_DEFAULT, function(p) {
      SRModel.set({ default_address_idx: p.index });
    });

    // ── 订单备注 ──
    dispatch.onM(CMD.ORDER_NOTE_SAVE, function(p) {
      SRModel.set({ order_note: p.note });
    });

    // ── 生命周期：出口写盘 ──
    dispatch.onM(CMD.PAGE_EXIT, function() {
      SRModel.persistOnExit();
    });

    console.log('[SRModel] M layer command handlers registered');
  }

  // 自动注册
  registerCommands();

})();
